import type { TableColumn } from '@consta/uikit/Table'
import { Table } from '@consta/uikit/Table'
import { Text } from '@consta/uikit/Text'
import { selectPreferredName, useAppSelector } from '@core/core/store'
import { parseDate, relativeDateFormatter } from '@core/core/utils'
import { createUrlTo } from '@library/model'
import type { ProjectId } from 'common/types'
import type { FC } from 'react'
import { useNavigate } from 'react-router-dom'

import { Published } from '../Published'
import type { ProjectDetailExtended, ProjectsViewProps } from '../types'
import { IconsCell } from './IconsCell'
import css from './ProjectsGridView.css'

const SIZE_COLUMN_TITLE = 'Размер данных, ГБ'

export const ProjectsGridView: FC<ProjectsViewProps> = ({ projects, toggleFavorite }) => {
  const navigate = useNavigate()

  const username = useAppSelector(selectPreferredName)

  const onCellClick = (projectId: ProjectId) => {
    navigate(createUrlTo({ base: 'Project', path: projectId }))
  }

  const renderNameCell = (row: ProjectDetailExtended) => (
    <Text
      view="link"
      cursor="pointer"
      onClick={() => {
        onCellClick(row.id)
      }}>
      {row.name}
    </Text>
  )

  const columnDefs: TableColumn<ProjectDetailExtended>[] = [
    {
      accessor: 'name',
      title: 'Название',
      width: 160,
      renderCell: renderNameCell,
    },
    {
      accessor: 'companyName',
      width: 160,
      title: 'ДО',
    },
    {
      accessor: 'fieldName',
      width: 160,
      title: 'Месторождение',
    },
    {
      accessor: 'changedAt',
      width: 160,
      title: 'Дата изменения',
      renderCell: renderDateCell,
    },
    {
      accessor: 'createdBy',
      width: 160,
      title: 'Автор',
    },
    {
      accessor: 'size',
      title: SIZE_COLUMN_TITLE,
      renderCell: ({ size }) => size,
    },
    {
      accessor: 'state',
      title: 'Статус',
      renderCell: ({ state, createdBy }) => (
        <Published state={state} isOwned={createdBy === username} />
      ),
    },
    {
      accessor: 'status',
      title: '',
      renderCell: row => renderIconsCell(row, toggleFavorite),
    },
  ]

  return (
    <div className={css.projectsGrid}>
      <Table
        className={css.table}
        size="m"
        rows={projects}
        columns={columnDefs}
        emptyRowsPlaceholder={<Text view="primary">Пусто</Text>}
        borderBetweenRows={true}
        verticalAlign="center"
        getAdditionalClassName={getAdditionalClassName}
      />
    </div>
  )
}

const renderDateCell = (row: ProjectDetailExtended) => {
  const changedAtDate = row.changedAt
  if (changedAtDate) {
    return (
      <Text view="primary">{relativeDateFormatter.relativeDate(parseDate(changedAtDate))}</Text>
    )
  }
}

const renderIconsCell = (
  row: ProjectDetailExtended,
  toggleFavorite: (projectId: ProjectId) => void,
) => <IconsCell params={row} toggleFavorite={toggleFavorite} />

const getAdditionalClassName = (item: {
  row: ProjectDetailExtended
  column: TableColumn<ProjectDetailExtended>
}) => {
  const size = Number(item.row.size)
  return !Number.isNaN(size) && size > 200 && item.column.title === SIZE_COLUMN_TITLE
    ? css.errorSize
    : ''
}
